import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "@/context/FormContext";
import { useLanguageContext } from "@/context/LanguageContext";
import { showToast } from "@int-traderjoes/ui-components-library";
import { getMandatoryFields } from "@/utils/helper";
import { getMainDetailsPermissions } from "@/static/permissions/active-projects/MainDetails.permissions";
import { IMainDetailsForm } from "@/types/active-projects/MainDetails.types";
import { useAuth } from "@/context/AuthContext";
import { getUserRole } from "@/utils/roleBasedFieldPermissions";
import Constants from "@/static/constants/constants.json";
import { isProduceRacMainDetailsLayout, shouldShowProduceRacMainDetailsPricingFields } from "@/components/ActiveProjects/MainDetails/utils/produceRacMainDetailsHelper";

interface ValidationResult {
  vendor: boolean;
  tj: boolean;
  overall: boolean;
}

export type MainDetailsFormErrors = {
  productSpecificationInformation: string[];
  productRelatedInformation: string[];
  vendorRelatedInformation: string[];
  vendorFacilityInformation: string[];
  photosofItemInRetailPackaging: string[];
};

const EMPTY_MAIN_DETAILS_FORM_ERRORS: MainDetailsFormErrors = {
  productSpecificationInformation: [],
  productRelatedInformation: [],
  vendorRelatedInformation: [],
  vendorFacilityInformation: [],
  photosofItemInRetailPackaging: [],
};

const isEmptyFieldValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

/** Number of flavors/varieties: the dropdown offers 1-12, so blank or the default "0" means nothing selected. */
const isEmptyFlavorsCount = (value: unknown): boolean =>
  isEmptyFieldValue(value) || String(value).trim() === "0";

/**
 * Whether an errored field now holds a valid value, using the same "not empty" rule as validateMainDetailsForm.
 * `true` = fixed, `false` = still invalid, `null` = can't tell from the form (e.g. photos) -> leave the error alone.
 * Vendor facility errors are keyed `${field}-${index}`.
 */
const isFieldFixed = (section: string, fieldName: string, form: any): boolean | null => {
  if (!form) return null;
  if (
    section === "productSpecificationInformation" ||
    section === "productRelatedInformation" ||
    section === "vendorRelatedInformation"
  ) {
    if (section === "productRelatedInformation" && fieldName === "numberOfFlavorsVarieties") {
      return !isEmptyFlavorsCount(form[section]?.[fieldName]);
    }
    return !isEmptyFieldValue(form[section]?.[fieldName]);
  }
  if (section === "vendorFacilityInformation") {
    const match = /^(.*)-(\d+)$/.exec(fieldName);
    if (!match || !Array.isArray(form.vendorFacilityInformation)) return null;
    const baseField = match[1] ?? "";
    const facility = form.vendorFacilityInformation[Number(match[2])];
    if (!facility) return true; // facility row no longer exists
    const value = facility[baseField];
    if (isEmptyFieldValue(value)) return false;
    // Zip code is also invalid when too short (see validateMainDetailsForm)
    if (baseField === "zipCodePostalCode") return String(value).trim().length >= Constants.ZIP_CODE_MIN_LENGTH;
    return true;
  }
  return null;
};

/** Drops the errors of fields that now hold a valid value; returns the same object when nothing changed. */
const removeFixedErrors = (errors: MainDetailsFormErrors, form: any): MainDetailsFormErrors => {
  let changed = false;
  const next = { ...errors };
  (Object.keys(errors) as Array<keyof MainDetailsFormErrors>).forEach((section) => {
    const list = errors[section] ?? [];
    const kept = list.filter((fieldName) => isFieldFixed(section, fieldName, form) !== true);
    if (kept.length !== list.length) {
      next[section] = kept;
      changed = true;
    }
  });
  return changed ? next : errors;
};

interface UseMainDetailsValidationReturn {
  validateMainDetailsForm: (showToasts?: boolean) => ValidationResult;
  mainDetailsFormErrors: {
    productSpecificationInformation: string[];
    productRelatedInformation: string[];
    vendorRelatedInformation: string[];
    vendorFacilityInformation: string[];
    photosofItemInRetailPackaging: string[];
  };
  /**
   * Snapshot of the last validation result. Unlike `mainDetailsFormErrors` it is NOT emptied by
   * `clearValidationErrors()` (Save/Next clear the highlighting), only by field edits (clearFieldError/setFieldError)
   * and by the next validateMainDetailsForm() run. Used for the invalid-fields alert.
   */
  lastValidationErrors: MainDetailsFormErrors;
  setMainDetailsFormErrors: React.Dispatch<React.SetStateAction<{
    productSpecificationInformation: string[];
    productRelatedInformation: string[];
    vendorRelatedInformation: string[];
    vendorFacilityInformation: string[];
    photosofItemInRetailPackaging: string[];
  }>>;
  clearValidationErrors: () => void;
  hasFieldError: (section: keyof IMainDetailsForm | 'photosofItemInRetailPackaging', fieldName: string) => boolean;
  clearFieldError: (section: keyof IMainDetailsForm | 'photosofItemInRetailPackaging', fieldName: string) => void;
  setFieldError: (section: keyof IMainDetailsForm | 'photosofItemInRetailPackaging', fieldName: string) => void;
  isFieldConditionallyMandatory: (section: string, field: string) => boolean;
}

export const useMainDetailsValidation = (): UseMainDetailsValidationReturn => {
  const { languageText } = useLanguageContext();
  const { mainDetailsForm, photosofItemInRetailPackaging } = useFormContext();
  const { userDetails } = useAuth();

  const errorTexts = languageText?.MESSAGES?.VALIDATION_MESSAGES;
  const toastMessages = languageText?.MESSAGES?.TOAST_MESSAGES;

  // Memoize user role check
  const isVendor = useMemo(() => getUserRole(userDetails) === 'VENDOR', [userDetails]);

  // Helper function to check if a field is conditionally mandatory
  const isFieldConditionallyMandatory = useCallback((section: string, field: string): boolean => {
    const specificationType = mainDetailsForm?.productSpecificationInformation?.productSpecificationType;
    
    // Normalize specification type for consistent checking
    const normalizedSpecType = specificationType?.toLowerCase().trim();
    const isPetFoods = normalizedSpecType === "pet_products" || normalizedSpecType === "pet_care" || 
                      normalizedSpecType === "pet foods" || normalizedSpecType?.includes("pet");
    const isAllBranded = normalizedSpecType === "all branded products" || normalizedSpecType?.includes("branded");
    // Note: Check Pet Foods BEFORE Food to avoid "pet foods" matching "food"
    const isFood = !isPetFoods && !isAllBranded && (normalizedSpecType === "fnb" || normalizedSpecType === "food & beverages" || normalizedSpecType?.includes("food"));
    const isHABA = normalizedSpecType === "haba" || normalizedSpecType?.includes("haba");
    const isProduceRac = isProduceRacMainDetailsLayout(specificationType);
    
    // Artwork Deadline: Mandatory only for TJ users (role-based), optional for vendor
    if (section === 'productSpecificationInformation' && field === 'artworkDeadline') {
      const permissions = getMainDetailsPermissions(userDetails, specificationType);
      const artworkDeadlinePermission = permissions.productSpecificationInformation?.artworkDeadline;
      const result = artworkDeadlinePermission?.isMandatory || false;
      return result;
    }
    
    // Buyer: Mandatory for TJ and vendor (role-based via permissions)
    if (section === 'productRelatedInformation' && field === 'buyer') {
      const permissions = getMainDetailsPermissions(userDetails, specificationType);
      const buyerPermission = permissions.productRelatedInformation?.buyer;
      const result = buyerPermission?.isMandatory || false;
      return result;
    }
    
    // Traffic Manager: Mandatory only for TJ users (role-based), optional for vendor
    if (section === 'productRelatedInformation' && field === 'trafficManager') {
      const permissions = getMainDetailsPermissions(userDetails, specificationType);
      const trafficManagerPermission = permissions.productRelatedInformation?.trafficManager;
      const result = trafficManagerPermission?.isMandatory || false;
      return result;
    }
    
    // Food Safety/HACCP Plan: Mandatory for Food & Beverages, HABA, and Produce RAC
    if (section === 'productRelatedInformation' && field === 'foodSaftyHaccpPlan') {
      const result = isFood || isHABA || isProduceRac;
      return result;
    }
    
    // FTL List: Mandatory for Food & Beverages and Produce RAC
    if (section === 'productRelatedInformation' && field === 'ftlList') {
      const result = isFood || isProduceRac;
      return result;
    }
    
    // Project Type: Mandatory for TJ users only, optional for vendor
    if (section === 'productRelatedInformation' && field === 'projectType') {
      const permissions = getMainDetailsPermissions(userDetails, specificationType);
      const projectTypePermission = permissions.productRelatedInformation?.projectType;
      return projectTypePermission?.isMandatory || false;
    }
    
    // Launch Date: Mandatory for TJ users only, optional for vendor
    if (section === 'productRelatedInformation' && field === 'launchDate') {
      const permissions = getMainDetailsPermissions(userDetails, specificationType);
      const launchDatePermission = permissions.productRelatedInformation?.launchDate;
      return launchDatePermission?.isMandatory || false;
    }
    
    // Regulatory Governance: Mandatory for TJ users only, optional for vendor
    if (section === 'productRelatedInformation' && field === 'regulatoryGovernance') {
      const permissions = getMainDetailsPermissions(userDetails, specificationType);
      const regulatoryGovernancePermission = permissions.productRelatedInformation?.regulatoryGovernance;
      return regulatoryGovernancePermission?.isMandatory || false;
    }
    
    // National Drug Code Number: Optional (HABA only, Both TJ and Vendor can edit)
    if (section === 'productRelatedInformation' && field === 'nationalDrugCodeNumber') {
      return false; // Optional field
    }

    // Certified Organic: Mandatory for Produce RAC only (TJ Label and Branded)
    if (section === 'productRelatedInformation' && field === 'certifiedOrganic') {
      return isProduceRac;
    }

    // Retail Price: Mandatory for TJ users on applicable Produce RAC layouts only
    if (section === 'productRelatedInformation' && field === 'retailPrice') {
      if (isVendor) {
        return false;
      }
      return shouldShowProduceRacMainDetailsPricingFields(
        specificationType,
        mainDetailsForm?.productSpecificationInformation?.opportunityBuy
      );
    }

    // Product Size / Case Pack: Mandatory for applicable Produce RAC layouts only
    if (
      section === 'productRelatedInformation' &&
      (field === 'productSize' || field === 'casePack')
    ) {
      return shouldShowProduceRacMainDetailsPricingFields(
        specificationType,
        mainDetailsForm?.productSpecificationInformation?.opportunityBuy
      );
    }

    // Is Multi-Pack Product: Mandatory for standard layouts only (hidden for Produce RAC)
    if (section === 'productRelatedInformation' && field === 'isMultiPackProduct') {
      return !isProduceRac;
    }

    // Number of Flavors/Varieties: Mandatory only when multi-pack is yes (not for Produce RAC)
    if (section === 'productRelatedInformation' && field === 'numberOfFlavorsVarieties') {
      if (isProduceRac) {
        return false;
      }
      return mainDetailsForm?.productRelatedInformation?.isMultiPackProduct === 'yes';
    }
    
    // Artwork Project ID: Mandatory for ALL specification types
    if (section === 'productSpecificationInformation' && field === 'artworkProjectId') {
      return true;
    }
    
    // For all other fields, use the default mandatory setting from permissions
    return true;
  }, [
    mainDetailsForm?.productSpecificationInformation?.productSpecificationType,
    mainDetailsForm?.productSpecificationInformation?.opportunityBuy,
    mainDetailsForm?.productRelatedInformation?.isMultiPackProduct,
    userDetails,
  ]);

  // TJ-only mandatory fields (buyer is shared — mandatory for TJ and vendor when permissions allow)
  const getTJOnlyFields = useCallback((): string[] => {
    return ['artworkDeadline', 'trafficManager', 'projectType', 'launchDate', 'regulatoryGovernance'];
  }, []);

  // Memoize mandatory fields for each section based on user role
  const mandatoryFields = useMemo(() => {
    const specificationType = mainDetailsForm?.productSpecificationInformation?.productSpecificationType;
    const permissions = getMainDetailsPermissions(userDetails, specificationType);
    
    // Helper to merge mandatory fields with TJ-only fields
    const mergeTJFields = (mandatoryList: string[], tjFields: string[]) => 
      Array.from(new Set([...mandatoryList, ...tjFields]));

    const productRelatedMandatory = getMandatoryFields(permissions.productRelatedInformation);
    const buyerMandatory = permissions.productRelatedInformation?.buyer?.isMandatory === true;
    const productRelatedWithBuyer =
      buyerMandatory && !productRelatedMandatory.includes('buyer')
        ? [...productRelatedMandatory, 'buyer']
        : productRelatedMandatory;
    const certifiedOrganicMandatory =
      permissions.productRelatedInformation?.certifiedOrganic?.isMandatory === true;
    const productRelatedWithCertifiedOrganic =
      certifiedOrganicMandatory && !productRelatedWithBuyer.includes('certifiedOrganic')
        ? [...productRelatedWithBuyer, 'certifiedOrganic']
        : productRelatedWithBuyer;

    const produceRacPricingMandatoryFields = ['retailPrice', 'productSize', 'casePack'] as const;
    const pricingFieldsMandatory = produceRacPricingMandatoryFields.some(
      field => permissions.productRelatedInformation?.[field]?.isMandatory === true
    );
    const productRelatedWithPricingFields = pricingFieldsMandatory
      ? produceRacPricingMandatoryFields.reduce(
          (fields, field) =>
            fields.includes(field) ? fields : [...fields, field],
          productRelatedWithCertifiedOrganic
        )
      : productRelatedWithCertifiedOrganic;
    
    return {
      productSpecificationInformation: mergeTJFields(
        getMandatoryFields(permissions.productSpecificationInformation),
        ['artworkDeadline']
      ),
      productRelatedInformation: mergeTJFields(
        productRelatedWithPricingFields,
        ['trafficManager', 'projectType', 'launchDate', 'regulatoryGovernance']
      ),
      vendorRelatedInformation: getMandatoryFields(permissions.vendorRelatedInformation),
      vendorFacilityInformation: getMandatoryFields(permissions.vendorFacilityInformation),
      photosofItemInRetailPackaging: getMandatoryFields(permissions.photosofItemInRetailPackaging)
    };
  }, [userDetails, mainDetailsForm?.productSpecificationInformation?.productSpecificationType]);

  // State for errors
  const [mainDetailsFormErrors, setMainDetailsFormErrors] = useState<{
    productSpecificationInformation: string[];
    productRelatedInformation: string[];
    vendorRelatedInformation: string[];
    vendorFacilityInformation: string[];
    photosofItemInRetailPackaging: string[];
  }>({
    productSpecificationInformation: [],
    productRelatedInformation: [],
    vendorRelatedInformation: [],
    vendorFacilityInformation: [],
    photosofItemInRetailPackaging: []
  });

  // Last validation result, kept when the highlighting errors are cleared (see `lastValidationErrors`).
  const [lastValidationErrors, setLastValidationErrors] = useState<MainDetailsFormErrors>(EMPTY_MAIN_DETAILS_FORM_ERRORS);

  const validateMainDetailsForm = useCallback((showToasts: boolean = true): ValidationResult => {
    if (!mainDetailsForm) {
      if (showToasts) {
        showToast({
          type: "error",
          message: toastMessages?.MAIN_DETAILS_FORM_DATA_NOT_AVAILABLE_ERROR
        });
      }
      return { vendor: false, tj: false, overall: false };
    }

    let isFormValid = true;
    let isVendorValid = true;
    let isTJValid = true;
    const tjOnlyFields = getTJOnlyFields();
    
    const newErrors = {
      productSpecificationInformation: [] as string[],
      productRelatedInformation: [] as string[],
      vendorRelatedInformation: [] as string[],
      vendorFacilityInformation: [] as string[],
      photosofItemInRetailPackaging: [] as string[]
    };
    
    // Permissions (used to avoid flagging non-editable fields as invalid)
    const specificationType = mainDetailsForm?.productSpecificationInformation?.productSpecificationType;
    const permissions = getMainDetailsPermissions(userDetails, specificationType);

    // Validate Product Specification Information
    const productSpecInfo = mainDetailsForm.productSpecificationInformation;
    if (productSpecInfo) {
      mandatoryFields.productSpecificationInformation.forEach((field) => {
        // Check if field is conditionally mandatory
        const isConditionallyMandatory = isFieldConditionallyMandatory('productSpecificationInformation', field);
        const isTJOnlyField = tjOnlyFields.includes(field);
        
        // Skip validation only if NOT conditionally mandatory AND NOT a TJ-only field
        if (!isConditionallyMandatory && !isTJOnlyField) {
          return; // Skip validation for this field
        }
        
        const value = productSpecInfo[field as keyof typeof productSpecInfo];
        const isEmpty = 
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "");

        if (isEmpty) {
          const isTJOnly = tjOnlyFields.includes(field);
          
          // Push error only if field is mandatory for current user
          if (!isTJOnly || !isVendor) {
            newErrors.productSpecificationInformation.push(field);
            isFormValid = false;
          }
          
          // Track TJ vs Vendor validation separately
          if (isTJOnly) {
            // TJ-only field: only affects TJ validation
            isTJValid = false;
          } else {
            // Shared mandatory field: affects BOTH vendor and TJ validation
            isVendorValid = false;
            isTJValid = false;
          }
        }
      });
    }

    // Validate Product Related Information
    const productRelatedInfo = mainDetailsForm.productRelatedInformation;
    if (productRelatedInfo) {
      mandatoryFields.productRelatedInformation.forEach((field) => {
        // Check if field is conditionally mandatory
        const isConditionallyMandatory = isFieldConditionallyMandatory('productRelatedInformation', field);
        const isTJOnlyField = tjOnlyFields.includes(field);
        
        // Skip validation only if NOT conditionally mandatory AND NOT a TJ-only field
        if (!isConditionallyMandatory && !isTJOnlyField) {
          return; // Skip validation for fields that are not conditionally mandatory
        }
        
        const value = productRelatedInfo[field as keyof typeof productRelatedInfo];
        const isEmpty = 
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "") ||
          (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
          const isTJOnly = tjOnlyFields.includes(field);
          
          // Push error only if field is mandatory for current user
          if (!isTJOnly || !isVendor) {
            newErrors.productRelatedInformation.push(field);
            isFormValid = false;
          }
          
          // Track TJ vs Vendor validation separately
          if (isTJOnly) {
            // TJ-only field: only affects TJ validation
            isTJValid = false;
          } else {
            // Shared mandatory field: affects BOTH vendor and TJ validation
            isVendorValid = false;
            isTJValid = false;
          }
        }
      });
    }

    // Multi-pack: isMultiPackProduct required (yes or no must be selected) — not for Produce RAC
    if (
      !isProduceRacMainDetailsLayout(specificationType) &&
      productRelatedInfo &&
      (!productRelatedInfo.isMultiPackProduct ||
        !["yes", "no"].includes(productRelatedInfo.isMultiPackProduct))
    ) {
      newErrors.productRelatedInformation.push("isMultiPackProduct");
      isFormValid = false;
      isVendorValid = false;
      isTJValid = false;
    }

    // Multi-pack: numberOfFlavorsVarieties required when isMultiPackProduct is yes — not for Produce RAC
    if (
      !isProduceRacMainDetailsLayout(specificationType) &&
      productRelatedInfo?.isMultiPackProduct === "yes" &&
      isEmptyFlavorsCount(productRelatedInfo?.numberOfFlavorsVarieties)
    ) {
      newErrors.productRelatedInformation.push("numberOfFlavorsVarieties");
      isFormValid = false;
      isVendorValid = false;
      isTJValid = false;
    }

    // Validate Vendor Related Information
    const vendorRelatedInfo =   mainDetailsForm.vendorRelatedInformation;
    if (vendorRelatedInfo) {
      mandatoryFields.vendorRelatedInformation.forEach((field) => {
        const value = vendorRelatedInfo[field as keyof typeof vendorRelatedInfo];
        const isEmpty = 
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "") ||
          (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
          newErrors.vendorRelatedInformation.push(field);
          isFormValid = false;
          // Vendor Related Information fields are required for both vendor and TJ users
          isVendorValid = false;
          isTJValid = false;
        }
      });
    }

    // Validate Vendor Facility Information
    const vendorFacilityInfo = mainDetailsForm.vendorFacilityInformation;
    if (Array.isArray(vendorFacilityInfo) && vendorFacilityInfo.length > 0) {
      // Validate all facilities for mandatory fields
      vendorFacilityInfo.forEach((facility, index) => {
        if (facility) {
          mandatoryFields.vendorFacilityInformation.forEach((field) => {
            // Skip validation for fields user can't edit (prevents impossible required errors)
            const fieldPermission = (permissions as any)?.vendorFacilityInformation?.[field];
            if (fieldPermission && fieldPermission.isEditable === false) {
              return;
            }

            const value = facility[field as keyof typeof facility];
            const isEmpty = 
              value === undefined ||
              value === null ||
              (typeof value === "string" && value.trim() === "");

            if (isEmpty) {
              // Use the correct field naming convention: fieldName-index
              newErrors.vendorFacilityInformation.push(`${field}-${index}`);
              isFormValid = false;
              // Vendor Facility Information fields are required for both vendor and TJ users
              isVendorValid = false;
              isTJValid = false;
            }
          });
          
          // Validate zip code length separately (regardless of whether it's mandatory)
          const zipValue = (facility.zipCodePostalCode ?? "").trim();
          if (zipValue && zipValue.length < Constants.ZIP_CODE_MIN_LENGTH) {
            if (!newErrors.vendorFacilityInformation.includes(`zipCodePostalCode-${index}`)) {
              newErrors.vendorFacilityInformation.push(`zipCodePostalCode-${index}`);
            }
            isFormValid = false;
            // Zip code validation affects both vendor and TJ users
            isVendorValid = false;
            isTJValid = false;
            if (showToasts) {
              showToast({
                type: "error",
                message: languageText?.VALIDATION_MESSAGES?.ZIP_CODE_MIN_LENGTH_ERROR || `Zip code must be at least ${Constants.ZIP_CODE_MIN_LENGTH} characters long.`,
              });
            }
          }
        }
      });
    }

    // Validate Photos of Item in Retail Packaging - USE SAME SOURCE AS getExistingFileData
    if (mandatoryFields.photosofItemInRetailPackaging.length > 0) {
      mandatoryFields.photosofItemInRetailPackaging.forEach((field) => {
        // Method 1: Check FormContext (current approach - fails for 3 files)
        const contextFileData = photosofItemInRetailPackaging?.[field as keyof typeof photosofItemInRetailPackaging];
        
        // Method 2: Try to access the same data that getExistingFileData accesses
        // Let's try to simulate getExistingFileData logic directly
        let simulatedFileData: any = undefined;
        
        // Try to find the PhotosofItemInRetailPackaging component instance and call its getExistingFileData
        try {
          // Use a custom event to communicate with the PhotosofItemInRetailPackaging component
          const event = new CustomEvent('validatePhotoField', { 
            detail: { fieldKey: field, requestId: Date.now() } 
          });
          
          // Set up a temporary listener for the response
          let responseReceived = false;
          const responseHandler = (e: any) => {
            if (e.detail.requestId === (event as any).detail.requestId) {
              simulatedFileData = e.detail.fileData;
              responseReceived = true;
              document.removeEventListener('photoFieldResponse', responseHandler);
            }
          };
          
          document.addEventListener('photoFieldResponse', responseHandler);
          document.dispatchEvent(event);
          
          // Wait briefly for response (synchronous check)
          const startTime = Date.now();
          while (!responseReceived && Date.now() - startTime < 100) {
            // Wait for response
          }
        } catch (error) {
          // Silently handle errors
        }
        
        // CRITICAL FIX: ONLY use the component's response, ignore stale FormContext
        // The component always has the most current state
        const hasContextFile = !!(contextFileData?.id || contextFileData?.name);
        const hasSimulatedFile = !!(simulatedFileData?.id || simulatedFileData?.name);
        const fileExists = hasSimulatedFile; // Only trust the component's response

        if (!fileExists) {
          newErrors.photosofItemInRetailPackaging.push(field);
          isFormValid = false;
          // Photos are required for both vendor and TJ users
          isVendorValid = false;
          isTJValid = false;
        }
      });
    }

    // Update errors state
    setMainDetailsFormErrors(newErrors);
    setLastValidationErrors(newErrors);

    // Show toast messages for validation errors
    if (!isFormValid) {
      const errorSections = [];
      
      if (newErrors.productSpecificationInformation.length > 0) {
        errorSections.push("Product Specification Information");
      }
      if (newErrors.productRelatedInformation.length > 0) {
        errorSections.push("Product Related Information");
      }
      if (newErrors.vendorRelatedInformation.length > 0) {
        errorSections.push("Vendor Related Information");
      }
      if (newErrors.vendorFacilityInformation.length > 0) {
        errorSections.push("Vendor Facility Information");
      }
      if (newErrors.photosofItemInRetailPackaging.length > 0) {
        errorSections.push("Photos of Item in Retail Packaging");
      }

      if (showToasts) {
        showToast({
          type: "error",
          message: toastMessages?.MAIN_DETAILS_MANDATORY_FIELDS_ERROR?.replace('{errorSections}', errorSections.join(", "))
        });
      }
    }

    return {
      vendor: isVendorValid,
      tj: isTJValid,
      overall: isFormValid
    };
  }, [mainDetailsForm, mandatoryFields, errorTexts, toastMessages, isFieldConditionallyMandatory, getTJOnlyFields]);

  const clearValidationErrors = useCallback(() => {
    setMainDetailsFormErrors({
      productSpecificationInformation: [],
      productRelatedInformation: [],
      vendorRelatedInformation: [],
      vendorFacilityInformation: [],
      photosofItemInRetailPackaging: []
    });
  }, []);

  const hasFieldError = useCallback((section: keyof IMainDetailsForm | 'photosofItemInRetailPackaging', fieldName: string): boolean => {
    // Skip validation for revisionNotes as it's not mandatory and doesn't need error checking
    if (section === 'revisionNotes') return false;
    return mainDetailsFormErrors[section as keyof typeof mainDetailsFormErrors]?.includes(fieldName) || false;
  }, [mainDetailsFormErrors]);

  const clearFieldError = useCallback((section: keyof IMainDetailsForm | 'photosofItemInRetailPackaging', fieldName: string) => {
    // Skip validation for revisionNotes as it's not mandatory and doesn't need error checking
    if (section === 'revisionNotes') return;
    setMainDetailsFormErrors(prev => ({
      ...prev,
      [section]: prev[section as keyof typeof prev].filter(field => field !== fieldName)
    }));
    // Keep the snapshot in sync with the user's edits (a fixed field leaves the invalid-fields list).
    setLastValidationErrors(prev => ({
      ...prev,
      [section]: (prev[section as keyof typeof prev] ?? []).filter(field => field !== fieldName)
    }));
  }, []);

  // Keep the red highlight until the field is valid: whenever the form changes, drop the errors of fields
  // that now have a value. Fields no longer clear their error on focus, so this is what clears it.
  useEffect(() => {
    setMainDetailsFormErrors(prev => removeFixedErrors(prev, mainDetailsForm));
    setLastValidationErrors(prev => removeFixedErrors(prev, mainDetailsForm));
  }, [mainDetailsForm]);

  const setFieldError = useCallback((section: keyof IMainDetailsForm | 'photosofItemInRetailPackaging', fieldName: string) => {
    // Skip validation for revisionNotes as it's not mandatory and doesn't need error checking
    if (section === 'revisionNotes') return;
    setMainDetailsFormErrors(prev => ({
      ...prev,
      [section]: prev[section as keyof typeof prev].includes(fieldName) ? prev[section as keyof typeof prev] : [...prev[section as keyof typeof prev], fieldName]
    }));
    setLastValidationErrors(prev => {
      const current = prev[section as keyof typeof prev] ?? [];
      return { ...prev, [section]: current.includes(fieldName) ? current : [...current, fieldName] };
    });
  }, []);

  return {
    validateMainDetailsForm,
    mainDetailsFormErrors,
    lastValidationErrors,
    setMainDetailsFormErrors,
    clearValidationErrors,
    hasFieldError,
    clearFieldError,
    setFieldError,
    isFieldConditionallyMandatory
  };
};
